import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, PencilSimple } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import CitySelect from "@/components/CitySelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const EMPTY = { name: "", address: "", city: "" };

export default function BizBranches() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);

  const load = () => api.get("/business/branches").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setF(EMPTY); setOpen(true); };
  const openEdit = (b) => { setEditId(b.id); setF({ name: b.name || "", address: b.address || "", city: b.city || "" }); setOpen(true); };

  const save = async () => {
    if (!f.name.trim()) return toast.error("Branch name is required");
    try {
      if (editId) { await api.patch(`/business/branches/${editId}`, f); toast.success("Branch updated"); }
      else { await api.post("/business/branches", f); toast.success("Branch created"); }
      setOpen(false); setF(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!list) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-3xl flex items-center gap-2"><MapPin size={26} className="text-primary" /> Branches</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-branch-btn" onClick={openNew} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={18} className="mr-1" /> New branch</Button></DialogTrigger>
          <DialogContent className="bg-card border-black/10">
            <DialogHeader><DialogTitle className="font-display">{editId ? "Edit branch" : "New branch"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs text-muted-foreground">Branch name</Label><Input data-testid="branch-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              <div><Label className="text-xs text-muted-foreground">City</Label><CitySelect testid="branch-city" value={f.city} onChange={(v) => setF({ ...f, city: v })} /></div>
              <div><Label className="text-xs text-muted-foreground">Address (optional)</Label><Input data-testid="branch-address" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-1.5 bg-muted/40 border-black/10 h-11 rounded-xl" /></div>
              <Button data-testid="save-branch" onClick={save} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">{editId ? "Save changes" : "Create"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.map((b) => (
          <div key={b.id} data-testid="branch-card" className="card-surface p-5 flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center"><MapPin size={20} weight="duotone" className="text-primary" /></div>
            <div className="min-w-0 flex-1">
              <p className="font-display font-semibold">{b.name} {b.is_main && <span className="text-xs text-primary">· Main</span>}</p>
              <p className="text-xs text-muted-foreground truncate">{[b.city, b.address].filter(Boolean).join(" · ") || "No location set"}</p>
            </div>
            <button data-testid={`edit-branch-${b.id}`} onClick={() => openEdit(b)} className="text-muted-foreground hover:text-foreground p-1.5 shrink-0"><PencilSimple size={18} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
